//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
// const useStyles = makeStyles(theme => ({
//     root: {},
// }));

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const pixelRatio = window.devicePixelRatio || 1;

const useStyles = makeStyles(theme => ({
    container: { border: '2px solid #1D8A8B', padding: 20, borderRadius: 6 },
}));

function getResizedCanvas(canvas, newWidth, newHeight) {
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = newWidth;
    tmpCanvas.height = newHeight;
    const ctx = tmpCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, newWidth, newHeight);
    return tmpCanvas;
}

function generateDownload(previewCanvas, crop, setBlob) {
    if (!crop || !previewCanvas) {
        return;
    }
    const canvas = getResizedCanvas(previewCanvas, crop.width, crop.height);
    canvas.toBlob(blob => setBlob(blob), 'image/png', 1);
}

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AfitdBannerCropper = ({ file, setBlob }) => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [upImg, setUpImg] = useState();
    const [crop, setCrop] = useState({ unit: '%', width: 250, aspect: 1 });
    const [completedCrop, setCompletedCrop] = useState(null);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        const reader = new FileReader();
        reader.addEventListener('load', () => setUpImg(reader.result));
        reader.readAsDataURL(file);
    }, [file]);

    useEffect(() => {
        if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
            return;
        }
        const image = imgRef.current;
        const canvas = previewCanvasRef.current;
        const crop = completedCrop;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');

        canvas.width = crop.width * pixelRatio;
        canvas.height = crop.height * pixelRatio;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);
        generateDownload(previewCanvasRef.current, completedCrop, setBlob);
    }, [completedCrop, setBlob]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const onLoad = useCallback(img => {
        imgRef.current = img;

        const isLandscape = img.width > img.height;
        const aspect = 1;

        if (isLandscape) {
            const width = (img.height / img.width) * 100;
            const x = (100 - width) / 2;
            setCrop({
                unit: '%',
                width,
                x,
                aspect,
            });
        } else {
            const height = (img.width / img.height) * 100;
            const y = (100 - height) / 2;
            setCrop({
                unit: '%',
                height,
                y,
                aspect,
            });
        }

        return false; // Return false if you set crop state in here.
    }, []);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div className={classes.container}>
            <ReactCrop src={upImg} onImageLoaded={onLoad} crop={crop} onChange={c => setCrop(c)} onComplete={c => setCompletedCrop(c)} imageStyle={{ height: 250 }} />
            <canvas ref={previewCanvasRef} style={{ display: 'none', width: Math.round(completedCrop?.width ?? 0), height: Math.round(completedCrop?.height ?? 0) }} />
        </div>
    );
};
export default AfitdBannerCropper;
